/**
 * @fileoverview Zod schemas for the style guides domain.
 */

import { z } from 'zod';

export const GenreTemplateSchema = z.object({
    templateId: z.string(),
    name: z.string(),
    description: z.string(),
});

export const GenericTemplateSchema = z.object({
  templateId: z.string(),
  name: z.string(),
  tags: z.array(z.string()),
  description: z.string().optional(),
});

export const StyleGuideInfluencesSchema = z.object({
  genres: z.array(z.string()).optional(),
  writers: z.array(z.string()).optional(),
  universes: z.array(z.string()).optional(),
});

export const StyleGuideMetaSchema = z.object({
  derivedTone: z.array(z.string()),
  primaryGenre: z.string(),
  contentRating: z.string(),
  pacingProfile: z.string(),
  synthesisNotes: z.string(),
});

export const StyleGuideImportOriginSchema = z.object({
  projectId: z.string(),
  projectName: z.string(),
});

export const StyleGuideSchema = z.object({
  id: z.string(),
  name: z.string(),
  content: z.string(),
  influences: StyleGuideInfluencesSchema.optional(),
  
  // V2 Fields - The full "recipe" of the style guide
  
  // -- Section 2: Content & Fundamentals --
  sexualContent: z.number().min(0).max(4).optional(),
  physicalViolence: z.number().min(0).max(4).optional(),
  emotionalViolence: z.number().min(0).max(4).optional(),
  profanity: z.number().min(0).max(4).optional(),

  // -- Section 3: The Mixing Board --
  narrativeDistance: z.number().min(0).max(4).optional(),
  visualSpecificity: z.number().min(0).max(4).optional(),
  syntacticComplexity: z.number().min(0).max(4).optional(),
  vocabularyRegister: z.number().min(0).max(4).optional(),
  descriptionDensity: z.number().min(0).max(4).optional(),
  pacingCadence: z.number().min(0).max(4).optional(),
  expositionIntegration: z.number().min(0).max(4).optional(),
  dialogueTexture: z.number().min(0).max(4).optional(),
  emotionalSaturation: z.number().min(0).max(4).optional(),
  atmosphericWeight: z.number().min(0).max(4).optional(),

  // -- Section 4: Refinement & Guardrails --
  toneKeywords: z.array(z.string()).optional(),
  antiInfluences: z.array(z.string()).optional(),
  customBannedPhrases: z.array(z.string()).optional(),
  recommendedPhrases: z.array(z.string()).optional().describe("A 'Green List' of words/phrases characteristic of the desired style."),

  // -- AI-generated metadata --
  meta: StyleGuideMetaSchema.optional(),

  // -- Tracking fields --
  updatedAt: z.string().optional(),
  importedFrom: StyleGuideImportOriginSchema.optional(),

  // Legacy V1 fields (can be deprecated later)
  heatLevel: z.string().optional(),
  violenceLevel: z.string().optional(),
  languageLevel: z.string().optional(),
  pacingPreference: z.string().optional(),
  dialogueStyle: z.string().optional(),
});

// Template Influence Schema - This represents the INPUT format from template files
// (what comes FROM genre/writer/universe template files)
export const TemplateInfluenceProseSchema = z.object({
  camera_pov: z.string(),
  prose_mechanics: z.string(),
  flow: z.string(),
  tone: z.string(),
  explicit: z.string().optional(),
});

export const TemplateInfluenceSettingsSchema = z.object({
  narrativeDistance: z.number().min(0).max(4),
  visualSpecificity: z.number().min(0).max(4),
  syntacticComplexity: z.number().min(0).max(4),
  vocabularyRegister: z.number().min(0).max(4),
  descriptionDensity: z.number().min(0).max(4),
  pacingCadence: z.number().min(0).max(4),
  expositionIntegration: z.number().min(0).max(4),
  dialogueTexture: z.number().min(0).max(4),
  emotionalSaturation: z.number().min(0).max(4),
  atmosphericWeight: z.number().min(0).max(4),
  sexualContent: z.number().min(0).max(4),
  physicalViolence: z.number().min(0).max(4),
  emotionalViolence: z.number().min(0).max(4),
  profanity: z.number().min(0).max(4),
});

export const TemplateInfluenceSchema = z.object({
  settings: TemplateInfluenceSettingsSchema,
  prose: TemplateInfluenceProseSchema,
});

export type StyleGuide = z.infer<typeof StyleGuideSchema>;
export type StyleGuideInfluences = z.infer<typeof StyleGuideInfluencesSchema>;
export type StyleGuideImportOrigin = z.infer<typeof StyleGuideImportOriginSchema>;
export type GenreTemplate = z.infer<typeof GenreTemplateSchema>;
export type WriterTemplate = z.infer<typeof GenericTemplateSchema>;
export type UniverseTemplate = z.infer<typeof GenericTemplateSchema>;
export type TemplateInfluence = z.infer<typeof TemplateInfluenceSchema>;
export type TemplateInfluenceProse = z.infer<typeof TemplateInfluenceProseSchema>;
export type TemplateInfluenceSettings = z.infer<typeof TemplateInfluenceSettingsSchema>;
